<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import http from "@/services/http";

interface ClientAccount {
  id: number;
  company_name: string;
  current: boolean;
}

const props = defineProps<{ companyName: string }>();
const expanded = ref(true);
const currentAccount = computed(() => accounts.value.find(account => account.current));
const otherAccounts = computed(() => accounts.value.filter(account => !account.current));
const currentName = computed(() => currentAccount.value?.company_name || props.companyName);
const avatarColors = ["bg-orange-700", "bg-teal-700", "bg-violet-600", "bg-rose-700"];
const router = useRouter();
const accounts = ref<ClientAccount[]>([]);
const switchingId = ref<number | null>(null);
const error = ref("");

function initials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase() || "?";
}

async function loadAccounts() {
  error.value = "";
  try {
    const { data } = await http.get<{ accounts: ClientAccount[] }>("/api/auth/client-profiles");
    accounts.value = data.accounts;
  } catch {
    error.value = "Unable to load your profiles.";
  }
}

async function switchProfile(account: ClientAccount) {
  if (account.current || switchingId.value !== null) return;
  switchingId.value = account.id;
  error.value = "";
  try {
    await http.post("/api/auth/client-profiles/switch", { client_id: account.id });
    try {
      localStorage.setItem("dmsuite:client-profile-switched", String(Date.now()));
    } catch {
      // The current tab can still switch when browser storage is unavailable.
    }
    // Recreate stores, query caches, sockets and embedded apps for the new identity.
    window.location.replace(router.resolve("/").href);
  } catch {
    error.value = "Unable to switch profiles. Try again or sign in to refresh your accounts.";
    switchingId.value = null;
  }
}

onMounted(loadAccounts);
</script>

<template>
  <section
    aria-label="Switch profile"
    class="profile-switcher min-w-0 bg-[#eff3f9] p-3 text-left"
  >
    <button
      type="button"
      class="flex w-full items-center gap-3 rounded-t-[28px] bg-white px-3 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      :class="{ 'rounded-b-[28px]': !expanded || !otherAccounts.length }"
      :aria-expanded="otherAccounts.length ? expanded : undefined"
      :disabled="!otherAccounts.length || switchingId !== null"
      aria-controls="client-profile-options"
      @click="expanded = !expanded"
    >
      <span
        aria-hidden="true"
        class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-blue-500 bg-blue-50 text-lg font-semibold text-blue-700 ring-2 ring-white ring-offset-2 ring-offset-blue-100"
      >
        {{ initials(currentName) }}
      </span>
      <span class="min-w-0 flex-1">
        <span class="block break-words text-base font-medium leading-6 text-gray-900">{{ currentName }}</span>
      </span>
      <span
        v-if="otherAccounts.length"
        class="flex h-9 w-6 shrink-0 items-center justify-center rounded-full bg-[#e9eef6] text-gray-600"
      >
        <i
          aria-hidden="true"
          class="fas text-xs"
          :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
        <span class="sr-only">{{ expanded ? 'Hide profiles' : 'Show profiles' }}</span>
      </span>
    </button>
    <div
      v-show="expanded && otherAccounts.length"
      id="client-profile-options"
      class="mt-0.5 max-h-60 space-y-0.5 overflow-y-auto rounded-b-xl"
      :aria-busy="switchingId !== null"
    >
      <button
        v-for="(account, index) in otherAccounts"
        :key="account.id"
        type="button"
        :disabled="switchingId !== null"
        :aria-label="`Switch to ${account.company_name}, profile ${account.id}`"
        class="flex w-full items-center gap-3 rounded-sm bg-white px-4 py-3 text-left transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 disabled:cursor-wait disabled:opacity-60"
        @click="switchProfile(account)"
      >
        <span
          aria-hidden="true"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
          :class="avatarColors[index % avatarColors.length]"
        >
          {{ initials(account.company_name) }}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block break-words text-sm font-medium leading-5 text-gray-900">{{ account.company_name }}</span>
        </span>
        <i
          v-if="switchingId === account.id"
          aria-hidden="true"
          class="fas fa-spinner fa-spin shrink-0 text-xs text-blue-600 motion-reduce:animate-none"
        />
      </button>
    </div>
    <p
      v-if="switchingId !== null"
      role="status"
      class="px-3 pt-2 text-xs text-blue-600"
    >
      Opening your profile...
    </p>
    <p
      v-if="error"
      role="alert"
      class="mt-2 rounded-lg bg-red-50 p-2 text-xs leading-5 text-red-600"
    >
      {{ error }}
    </p>
    <button
      v-if="error && !accounts.length"
      type="button"
      class="mt-2 rounded px-2 py-1 text-xs font-semibold text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      @click="loadAccounts"
    >
      Retry
    </button>
  </section>
</template>
